import { useRef } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/ui-components/Button';

import useProductShowStore from 'stores/product_show_store/useProductShowStore';
import usePricingStore from 'stores/pricing_store/usePricingStore';

import { grey50, ctaGreen, white } from 'constants/variables';
import { useBasketAction } from 'hooks/useBasketAction';

import DeliveryStock from 'components/product_show/add_to_basket/delivery_stock';
import { HeaderVanIcon } from 'assets/icons';

const AddToBasket = ({
  stepId,
  addToBasketUrl,
  stockCode,
  brand,
  title,
  category,
  partCode,
  isCollectionOnly,
  type,
  isSimilarStock,
  deliveryStock,
  deliveryStockEta,
  specialOrderMessage,
  showStock,
}) => {
  const buttonRef = useRef(null);

  const {
    productStock,
    showBackOrderMessage,
    isAddingToBasket,
    openBackOrderModal,
  } = useProductShowStore();

  const { productPrice } = usePricingStore();

  const { triggerAction, forceAction } = useBasketAction({
    stepId,
    addToBasketUrl,
    productPrice,
    isReserveCollect: false,
    title,
    brand: brand || '',
    category,
    partCode,
    stockCode,
    isSimilarStock,
    type,
    stockLevel: productStock,
    onBackOrderRequired: () => {
      openBackOrderModal(() => {
        forceAction();
        buttonRef.current?.focus();
      });
    },
  });

  const isButtonAddingToBasket = isAddingToBasket === 'delivery';

  const isButtonDisabled =
    isButtonAddingToBasket ||
    productStock === '0' ||
    !productPrice ||
    isCollectionOnly;

  const isBackOrderProduct = showBackOrderMessage || productStock === 0;

  const buttonText = () => {
    switch (true) {
      case isCollectionOnly:
        return 'Collection Only';
      case isAddingToBasket === 'delivery':
        return 'Added to Basket';
      case isBackOrderProduct:
        return 'Place on Back Order';
      default:
        return 'Add for Delivery';
    }
  };

  const iconColor = isButtonAddingToBasket
    ? ctaGreen
    : isButtonDisabled
    ? grey50
    : white;

  const baseClasses =
    'js-product-action-btn product-action-btn cef-btn ff-body fw-600 fl fl-row ai-c fs-14 l-fs-16 pt-14 pb-14';
  const colorClass = isBackOrderProduct ? 'cef-btn--grey' : 'cef-btn--green';
  const disabledClass =
    isButtonAddingToBasket && !isCollectionOnly
      ? 'js-product-action-btn--disabled'
      : '';

  const buttonClass = `${baseClasses} ${colorClass} ${disabledClass}`;

  return (
    <div className="cef__add-to-basket fl fl-col">
      <Button
        innerRef={buttonRef}
        testId={type ? `${type}-add-to-basket` : 'add-to-basket'}
        disabled={isButtonDisabled}
        handleClick={triggerAction}
        className={buttonClass}>
        <HeaderVanIcon width={28} height={28} fillColor={iconColor} />
        {buttonText()}
      </Button>

      {showStock && !isCollectionOnly && (
        <DeliveryStock
          deliveryStock={deliveryStock}
          deliveryStockEta={deliveryStockEta}
          specialOrderMessage={specialOrderMessage}
          isCollectionOnly={isCollectionOnly}
        />
      )}
    </div>
  );
};

AddToBasket.propTypes = {
  stepId: PropTypes.string,
  addToBasketUrl: PropTypes.string,
  stockCode: PropTypes.string,
  brand: PropTypes.string,
  title: PropTypes.string,
  category: PropTypes.string,
  partCode: PropTypes.string,
  isCollectionOnly: PropTypes.bool,
  type: PropTypes.string,
  isSimilarStock: PropTypes.bool,
  deliveryStock: PropTypes.number,
  deliveryStockEta: PropTypes.string,
  specialOrderMessage: PropTypes.string,
  showStock: PropTypes.bool,
};

export default AddToBasket;
